// Linear classifier over hashed char-n-gram + entity-tag features — a
// port of `tento/tento-event-classifier/src/model.rs`. Pure: no DOM, no
// I/O. The caller supplies the weight bytes (fetched and parsed once)
// and gets per-class scores / an argmax category back.
//
// Forward pass:
//   1. normalize(title) → tokens (entity_normalizer).
//   2. For each plain token: whole-token hash + char trigrams with
//      `<` / `>` word boundaries. For each entity token: the tag's
//      fixed key. All hashed into [0, nBuckets) via FNV-1a.
//   3. logits[c] = bias[c] + Σ over feature buckets b of weights[b, c].
//   4. Per-class sigmoid (independent, NOT softmax); argmax for the
//      single best label.
//
// f32 fidelity: the Rust model accumulates and sigmoids in f32. We
// store weights/bias/accumulators in `Float32Array` so every store
// rounds to f32 exactly as Rust does, and wrap sigmoid arithmetic in
// `Math.fround`. This keeps predictions bit-identical (modulo a
// possible <1 ULP difference in the `exp` library, far below any
// decision boundary). Verified by the parity test.

import type { Option } from "@tento-lona/sheets";
import { elementAt } from "@tento-lona/sheets/internal-sdk";
import type { EventCategory } from "../events/event-category-item.ts";
import { bucket } from "./fnv.ts";
import { normalize, type Token } from "./entity-normalizer.ts";

export const N_CLASSES = 10;
export const DEFAULT_N_BUCKETS = 16384;

// Class index → wire category string. This order is the Rust
// `EventCategory` enum discriminant order and MUST NOT be reordered —
// it is the meaning of the trained weight columns.
export const CLASSIFIER_CATEGORIES: readonly EventCategory[] = [
  "exercise",
  "work",
  "social",
  "food",
  "errands",
  "transportation",
  "creative",
  "nap",
  "shower",
  "break",
];

const MAGIC = [0x45, 0x56, 0x43, 0x4c]; // "EVCL"
const FORMAT_VERSION = 2;
const LEGACY_FORMAT_VERSION = 1;

/**
 * The weights of the event-category classifier: a linear model over hashed
 * feature buckets.
 */
export interface ClassifierModel {
  readonly nBuckets: number;
  readonly nClasses: number;
  readonly modelRevision: number;
  readonly weights: Float32Array; // row-major: weights[b * nClasses + c]
  readonly bias: Float32Array;
}

/** Extract per-token feature buckets into `out` (mirrors Rust `featurize_token`). */
function featurizeToken(tok: Token, out: number[], nBuckets: number): void {
  if (tok.kind === "entity") {
    out.push(bucket(tok.tag, nBuckets));
    return;
  }
  const s = tok.text;
  // Whole-token hash (high-signal whole words get a direct feature).
  out.push(bucket(s, nBuckets));
  // Char trigrams with word-boundary markers, over code points.
  const chars = ["<", ...Array.from(s), ">"];
  if (chars.length < 3) {
    out.push(bucket(chars.join(""), nBuckets));
    return;
  }
  for (let i = 0; i <= chars.length - 3; i++) {
    const trigram = elementAt(chars, i, TRIGRAM) + elementAt(chars, i + 1, TRIGRAM) +
      elementAt(chars, i + 2, TRIGRAM);
    out.push(bucket(trigram, nBuckets));
  }
}

/** What a checked read of a trigram's code point names. */
const TRIGRAM = "event-classifier/model: trigram code point";

/** What a checked read of a weight, bias or accumulator names. */
const WEIGHT = "event-classifier/model: weight / bias / class slot";

/** Title → bucket-index sequence, with duplicates (a repeated feature counts twice). */
export function featurize(title: string, nBuckets: number): number[] {
  const tokens = normalize(title);
  const out: number[] = [];
  for (const t of tokens) featurizeToken(t, out, nBuckets);
  return out;
}

/** Numerically stable sigmoid, mirroring the f32 two-branch Rust form. */
export function sigmoid(x: number): number {
  if (x >= 0) {
    const e = Math.fround(Math.exp(Math.fround(-x)));
    return Math.fround(1 / Math.fround(1 + e));
  }
  const e = Math.fround(Math.exp(x));
  return Math.fround(e / Math.fround(1 + e));
}

/** Raw logits for a single title (one per class). */
export function logits(model: ClassifierModel, title: string): Float32Array {
  const out = new Float32Array(N_CLASSES); // f32 accumulators
  const buckets = featurize(title, model.nBuckets);
  const nc = model.nClasses;
  const w = model.weights;
  for (const b of buckets) {
    const row = b * nc;
    for (let c = 0; c < N_CLASSES; c++) {
      // Float32Array store rounds to f32 — exactly Rust's `out[c] += ...`.
      out[c] = elementAt(out, c, WEIGHT) + elementAt(w, row + c, WEIGHT);
    }
  }
  for (let c = 0; c < N_CLASSES; c++) {
    out[c] = elementAt(out, c, WEIGHT) + elementAt(model.bias, c, WEIGHT);
  }
  return out;
}

/** Per-class sigmoid probabilities for a single title. */
export function scores(model: ClassifierModel, title: string): Float32Array {
  const l = logits(model, title);
  const out = new Float32Array(N_CLASSES);
  for (let c = 0; c < N_CLASSES; c++) out[c] = sigmoid(elementAt(l, c, WEIGHT));
  return out;
}

/** `(index, score)` — argmax of the sigmoid scores (first index wins on ties). */
export function argmax(model: ClassifierModel, title: string): { index: number; score: number } {
  const s = scores(model, title);
  let best = 0;
  let bestScore = elementAt(s, 0, WEIGHT);
  for (let c = 1; c < N_CLASSES; c++) {
    const score = elementAt(s, c, WEIGHT);
    if (score > bestScore) {
      best = c;
      bestScore = score;
    }
  }
  return { index: best, score: bestScore };
}

/**
 * Parse the canonical `.bin` weight file. Little-endian layout, mirrors
 * `Model::save`: magic `EVCL`, format version (v2 carries a
 * `model_revision` u32; legacy v1 does not), n_buckets, n_classes
 * (== 10), then f32 weights (row-major) and f32 bias.
 */
export function parseModel(bytes: Uint8Array): ClassifierModel {
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  let off = 0;
  for (let i = 0; i < MAGIC.length; i++) {
    if (view.getUint8(off + i) !== MAGIC[i]) {
      throw new Error(
        "event-classifier/model/bad-magic: not an EVCL file (an old ONNX model?)",
      );
    }
  }
  off += 4;

  const version = view.getUint32(off, true);
  off += 4;
  let modelRevision = 0;
  if (version === LEGACY_FORMAT_VERSION) {
    modelRevision = 0;
  } else if (version === FORMAT_VERSION) {
    modelRevision = view.getUint32(off, true);
    off += 4;
  } else {
    throw new Error(
      `event-classifier/model/version-mismatch: file v${version}, supported v${FORMAT_VERSION} (or legacy v${LEGACY_FORMAT_VERSION})`,
    );
  }

  const nBuckets = view.getUint32(off, true);
  off += 4;
  const nClasses = view.getUint32(off, true);
  off += 4;
  if (nClasses !== N_CLASSES) {
    throw new Error(
      `event-classifier/model/class-mismatch: file has ${nClasses} classes, expected ${N_CLASSES}`,
    );
  }

  const wLen = nBuckets * nClasses;
  const weights = new Float32Array(wLen);
  for (let i = 0; i < wLen; i++) {
    weights[i] = view.getFloat32(off, true);
    off += 4;
  }
  const bias = new Float32Array(nClasses);
  for (let i = 0; i < nClasses; i++) {
    bias[i] = view.getFloat32(off, true);
    off += 4;
  }

  return { nBuckets, nClasses, modelRevision, weights, bias };
}

/**
 * In-browser event-title classifier. Construct once from the trained
 * weight bytes (see the app-layer loader), then call `classify` /
 * `classifyWithScores` per title.
 */
export class EventClassifier {
  readonly #model: ClassifierModel;
  constructor(model: ClassifierModel) {
    this.#model = model;
  }

  static fromBytes(bytes: Uint8Array): EventClassifier {
    return new EventClassifier(parseModel(bytes));
  }

  /** Model revision the weights were trained at (for cache invalidation). */
  get modelRevision(): number {
    return this.#model.modelRevision;
  }

  /**
   * Best category for a title, or `null` for an empty/whitespace title
   * (where there is nothing to suggest). Non-empty titles always return
   * a category — gate on confidence via `classifyWithScores` if needed.
   */
  classify(title: string): Option<EventCategory> {
    if (title.trim().length === 0) return null;
    return elementAt(CLASSIFIER_CATEGORIES, argmax(this.#model, title).index, "EventClassifier.classify: category");
  }

  /** Full per-class probabilities, sorted descending. */
  classifyWithScores(title: string): Array<[EventCategory, number]> {
    const s = scores(this.#model, title);
    const pairs: Array<[EventCategory, number]> = [];
    for (let c = 0; c < N_CLASSES; c++) {
      pairs.push([
        elementAt(CLASSIFIER_CATEGORIES, c, "EventClassifier.classifyWithScores: category"),
        elementAt(s, c, WEIGHT),
      ]);
    }
    pairs.sort((a, b) => b[1] - a[1]);
    return pairs;
  }
}
